import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Navbar,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import logo from "assets/logo.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faCheckCircle,
  faXmarkCircle,
} from "@fortawesome/free-solid-svg-icons";
import { recoverPassword } from "../../api/auth";
import ConfirmationModal from "./ConfirmationModal";
import VerificationCodeModal from "./VerificationCodeModal";

const PasswordRecovery: React.FC = () => {
  const [email, setEmail] = useState("");
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");

  const navigate = useNavigate();

  /**
   * Handles changes in the email input field.
   * Updates the email state and validates the email format.
   * @param e - The input change event
   */
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setEmail(email);
    setIsValidEmail(validateEmail(email));
  };

  const handleConfirmationModalClose = () => {
    setShowConfirmationModal(false);
    if (!isError) {
      setShowVerificationModal(true);
    }
  };

  /**
   * Validates the email format using a regular expression.
   * @param email - The email string to validate
   * @returns True if the email is valid, false otherwise
   */
  const validateEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  /**
   * Handles the form submission for password recovery.
   * Sends a request to recover the password if the email is valid.
   * Shows a success or error modal based on the response.
   * @param e - The form submit event
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isValidEmail) {
      setIsLoading(true);
      try {
        const response = await recoverPassword(email);
        if (response.status === 200) {
          setIsError(false);
          setShowConfirmationModal(true);
        } else {
          setIsError(true);
          setShowConfirmationModal(true);
        }
      } catch (error) {
        setIsError(true);
        setShowConfirmationModal(true);
      } finally {
        setIsLoading(false);
      }
    }
  };



  /**
   * Navigates back to the admin login page.
   */
  const handleBack = () => {
    navigate("/adminLogin");
  };

  return (
    <>
      <Navbar style={{ backgroundColor: "#FBE584" }}>
        <Navbar.Brand href="#">
          <img src={logo} alt="Logo de la Boutique" style={{ width: "50px" }} />
        </Navbar.Brand>
      </Navbar>
      <Button variant="secondary" onClick={handleBack} className="back-button">
        <FontAwesomeIcon icon={faArrowLeft} />
        <span className="back-text" style={{ marginLeft: "5px" }}>
          Regresar
        </span>
      </Button>
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col xs={12} md={6} className="text-center">
            <img
              src={logo}
              alt="Logo"
              className="mb-4"
              style={{ width: "150px" }}
            />

            <h1 className="fw-bold mb-4">Olvidé mi contraseña</h1>

            <p className="fw-bold mb-4">
              <span>
                Ingresa el correo del cual quieres recuperar la contraseña.
              </span>
            </p>

            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Control
                  type="email"
                  placeholder="Correo electrónico"
                  value={email}
                  onChange={handleEmailChange}
                  isInvalid={!isValidEmail}
                  required
                  className="text-center w-75 mx-auto"
                />
                <Form.Control.Feedback type="invalid">
                  Correo electrónico incorrecto.
                </Form.Control.Feedback>
              </Form.Group>

              <Button 
                variant="primary" 
                type="submit" 
                className="w-20 mb-3"
                disabled={isLoading}
              >
                {isLoading ? "Enviando..." : "Continuar"}
              </Button>
            </Form>

            <Button
              variant="link"
              onClick={() => navigate("/adminLogin")}
              style={{ textDecoration: "none" }}
            >
              Iniciar sesión
            </Button>
          </Col>
        </Row>
      </Container>
      <ConfirmationModal
        show={showConfirmationModal}
        onHide={handleConfirmationModalClose}
        title="Confirmación"
        message={
          isError
            ? "Error al enviar el correo.\n Por favor, inténtelo nuevamente."
            : "Se ha enviado un correo electrónico al correo indicado, sigue las instrucciones para la recuperación de contraseña."
        }
        icon={isError ? faXmarkCircle : faCheckCircle}
        iconColor={isError ? "text-danger" : "text-success"}
      />
      <VerificationCodeModal
        show={showVerificationModal}
        onHide={() => setShowVerificationModal(false)}
        verificationCode={verificationCode}
        setVerificationCode={setVerificationCode}
        email={email}
      />
    </>
  );
};

export default PasswordRecovery;
